import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { Map, List } from 'immutable';
import classNames from 'classnames';

import Popup from '../../helpers/Popup';
import Button from '../../helpers/Button';
import Checkbox from '../../helpers/Checkbox';
import { ProjectMenu } from '../../Project/ProjectMenu';
import config from '../../../config';
import { ProjectSuggestionInput } from './ProjectSuggestionInput';
import { ProjectTextArea } from './ProjectTextArea';
import { ProjectTextInput } from './ProjectTextInput';
import { OneColumnTable } from './OneColumnTable';

import style from './style.module.css';

const DELETE_MENU_ID = 'projectDeleteContextMenu';
const TEMPLATE_MENU_ID = 'projectTemplateContextMenu';

const types = config.values.getIn(['project', 'types']);

/**
 * Context menus rendered into body: Draggable transforms the dialog, which would offset fixed-positioned menus.
 */
const ProjectMenus = ({ hasTemplates, onDelete, onDownload }) =>
  createPortal(
    <>
      <ProjectMenu id={DELETE_MENU_ID} onDelete={onDelete} />
      {hasTemplates && <ProjectMenu id={TEMPLATE_MENU_ID} onDelete={onDelete} onDownload={onDownload} isTemplate />}
    </>,
    document.body,
  );

/**
 * Popup for editing project details, industries, users and templates.
 *
 * @param props {Object}.
 * @param props.projectData {Immutable.Map} Project information data.
 * @param props.users {Immutable.List} User list.
 * @param props.industries {Immutable.List} Industry rows (`name`).
 * @param props.isIndustriesChanged {Boolean} Marks the Industries table as changed.
 * @param props.userSuggestions {Immutable.List} Suggestions for the edited user row.
 * @param props.templates {Immutable.List} Template list; the Templates table is hidden when omitted.
 * @param props.isBuyerHidden {Boolean} Hide the Buyer field.
 * @param props.common {Immutable.Map} Common object.
 * @param props.isSaving {Boolean} Saving status.
 * @param props.onSave {Function} Handle event of saving details.
 * @param props.onCancel {Function} Handle event of discarding changes.
 */
const ProjectDetailsPopupComponent = ({
  projectData,
  users,
  industries,
  isIndustriesChanged,
  userSuggestions,
  templates,
  isBuyerHidden = false,
  common,
  isSaving,
  onSave,
  onCancel,
  onReset,
  onTextChange,
  onFocus,
  onSuggestionClose,
  onSuggestionSelect,
  onSuggestionInputClick,
  onRowClick,
  onRowDoubleClick,
  onRowInsert,
  onDeleteRow,
  onDownload,
  onKeyPress,
  onTemplateUpdate,
}) => {
  const projectType = types.get('PROJECT');

  const footer = (
    <>
      <Button className="btn-primary" disabled={isSaving} onClick={onSave}>
        Save
      </Button>
      <Button onClick={onCancel}>
        Cancel
      </Button>
    </>
  );

  return (
    <Popup
      className={style.host}
      footer={footer}
      header="Edit project details"
      id="projectDetailsPopup"
      loading={isSaving}
    >
      <div className={style.content} onClick={onReset}>
        <div className={style.fields}>
          <ProjectTextInput field={projectData.get('category')} onTextChange={onTextChange} />
          <div className="form-group">
            <label className="field-label" htmlFor="projectTypeInput">
              Type
            </label>
            <input className="form-control" id="projectTypeInput" value={projectData.get('type') || ''} disabled />
          </div>
          <ProjectSuggestionInput
            label="HarvCo Lead"
            name="harvcoLead"
            onFocus={onFocus}
            onSuggestionClose={onSuggestionClose}
            onSuggestionInputClick={onSuggestionInputClick}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            row={projectData.get('harvcoLead')}
            suggestions={common.get('harvcoLeadSuggestions')}
          />
          <ProjectSuggestionInput
            label="Client Lead"
            name="clientLead"
            onFocus={onFocus}
            onSuggestionClose={onSuggestionClose}
            onSuggestionInputClick={onSuggestionInputClick}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            row={projectData.get('clientLead')}
            suggestions={common.get('clientLeadSuggestions')}
          />
          <ProjectSuggestionInput
            addon={
              <span className="input-group-addon">
                <a href={`/contact/${projectData.getIn(['exec', 'id'])}`} rel="noopener noreferrer" target="_blank">
                  <i className="fa fa-reply" title="Open in external tab" />
                </a>
              </span>
            }
            label="Executive"
            name="exec"
            onFocus={onFocus}
            onSuggestionClose={onSuggestionClose}
            onSuggestionInputClick={onSuggestionInputClick}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            row={projectData.get('exec')}
            suggestions={common.get('execSuggestions')}
          />
          {!isBuyerHidden && (
            <div className="form-group">
              <label className="field-label" htmlFor="projectBuyerInput">
                Buyer
              </label>
              <input
                className="form-control"
                id="projectBuyerInput"
                value={projectData.get('buyerName') || ''}
                disabled
              />
            </div>
          )}
        </div>
        <div
          className={classNames('group-checkbox', style.active, { changed: projectData.getIn(['active', 'dirty']) })}
        >
          <Checkbox
            checked={projectData.get('active')}
            label="Active"
            name="active"
            onChange={event => onTextChange({ event, data: { type: projectType } })}
            onClick={event => event.stopPropagation()}
          />
        </div>
        <ProjectTextArea
          field={projectData.get('details')}
          id="projectDetails"
          label="Project Scope"
          name="details"
          onTextChange={onTextChange}
        />
        <ProjectTextArea
          field={projectData.get('status')}
          id="projectStatus"
          label="Status"
          name="status"
          onTextChange={onTextChange}
        />
        <div className={style.tables}>
          <OneColumnTable
            changed={isIndustriesChanged}
            contextId={DELETE_MENU_ID}
            onRowDoubleClick={onRowDoubleClick}
            onRowInsert={onRowInsert}
            rows={industries}
            title="Industries"
            type={types.get('INDUSTRIES')}
            skipMenu
          />
          <OneColumnTable
            contextId={DELETE_MENU_ID}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            onRowInsert={onRowInsert}
            onSuggestionClose={onSuggestionClose}
            onSuggestionSelect={onSuggestionSelect}
            onTextChange={onTextChange}
            rows={users}
            suggestions={userSuggestions}
            title="Users"
            type={types.get('USERS')}
          />
          {templates && (
            <OneColumnTable
              contextId={TEMPLATE_MENU_ID}
              onKeyPress={onKeyPress}
              onRowClick={onRowClick}
              onRowInsert={onRowInsert}
              onTemplateUpdate={onTemplateUpdate}
              onTextChange={onTextChange}
              rows={templates}
              title="Templates"
              type={types.get('TEMPLATES')}
            />
          )}
        </div>
      </div>
      <ProjectMenus hasTemplates={!!templates} onDelete={onDeleteRow} onDownload={onDownload} />
    </Popup>
  );
};

ProjectDetailsPopupComponent.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  industries: PropTypes.instanceOf(List).isRequired,
  isBuyerHidden: PropTypes.bool,
  isIndustriesChanged: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDeleteRow: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onFocus: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func,
  onReset: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  onRowDoubleClick: PropTypes.func.isRequired,
  onRowInsert: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSuggestionClose: PropTypes.func.isRequired,
  onSuggestionInputClick: PropTypes.func.isRequired,
  onSuggestionSelect: PropTypes.func.isRequired,
  onTemplateUpdate: PropTypes.func,
  onTextChange: PropTypes.func.isRequired,
  projectData: PropTypes.instanceOf(Map).isRequired,
  templates: PropTypes.instanceOf(List),
  users: PropTypes.instanceOf(List).isRequired,
  userSuggestions: PropTypes.instanceOf(List).isRequired,
};

export const ProjectDetailsPopup = memo(ProjectDetailsPopupComponent);
